import React from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'

import {
  Build as BuildIcon,
  Home as HomeIcon,
  Print as PrintIcon,
  TrackChanges as TrackChangesIcon,
} from '@material-ui/icons'

import { DetailTableProps } from './types'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { SKULabel } from 'src/components/PrintLabel/SKULabel'
import { Notification } from 'src/components/Notification'

import { BooleanField, RelatedLinkedField, SkuUnitField, ValueField } from 'src/components/Table'
import { DetailComponent } from './base'
import { skuit } from 'src/services/skuit'

export const SkuDetailTable = (props: DetailTableProps) => {
  const { obj, refresh } = props
  const { print, renderPrintComponent } = useAsyncPrint()

  const { mutate: reconcile } = useMutation(skuit.sc.skus.reconcileFound, {
    onSuccess: () => {
      refresh?.()
      toast.success(() => <Notification message="Successfully reconciled found and lost units." />)
    },
    onError: () => {
      toast.error(() => <Notification message="Failed to reconcile found and lost." />)
    },
  })

  const { mutate: fix } = useMutation(skuit.sc.skus.fix, {
    onSuccess: () => {
      refresh?.()
      toast.success(() => <Notification message="Successfully fixed the SKU." />)
    },
    onError: () => {
      toast.error(() => (
        <Notification message="Failed to fix the SKU. Needs investigation (possible inconsistencies)." />
      ))
    },
  })

  return (
    <>
      {renderPrintComponent()}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={[
          {
            Icon: PrintIcon,
            actions: [
              {
                title: 'Print SKU Label',
                onClick: () => print((props) => <SKULabel {...props} obj={obj.data} />),
              },
            ],
          },
          {
            Icon: TrackChangesIcon,
            actions: [
              {
                title: 'Auto-Reconcile Lost & Found',
                onClick: () => reconcile(obj.data.guid as string),
              },
            ],
          },
          {
            Icon: BuildIcon,
            actions: [
              {
                title: 'Fix SKU',
                onClick: () => fix(obj.data.guid as string),
              },
            ],
          },
        ]}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'sku', label: 'SKU', renderer: ValueField },
          {
            id: 'productNmae',
            label: 'Product Name',
            ordering: 'product_name',
            renderer: ValueField,
          },
          {
            id: 'client',
            label: 'Client',
            ordering: 'client__name',
            renderer: RelatedLinkedField(ValueField, 'name'),
          },
          {
            id: 'product',
            label: 'Product',
            ordering: 'product__name',
            renderer: RelatedLinkedField(ValueField, 'name'),
          },
          {
            id: 'trackDateReceived',
            label: 'Track Date Units Received',
            ordering: 'track_date_received',
            renderer: BooleanField,
          },
          {
            id: 'unitOfMeasureDisplay',
            label: 'Unit of Measure',
            ordering: 'unit_of_measure',
            renderer: ValueField,
          },
          {
            id: 'numAvailable',
            label: 'Number of Units Available',
            ordering: 'num_available',
            renderer: SkuUnitField,
          },
          {
            id: 'numDamaged',
            label: 'Number of Damaged Units',
            ordering: 'num_damaged',
            renderer: SkuUnitField,
          },
          {
            id: 'numReserved',
            label: 'Number of Units in Orders',
            ordering: 'num_reserved',
            renderer: SkuUnitField,
          },
          {
            id: 'numAwaitingShipment',
            label: 'Number of Units Awaiting Shipment',
            ordering: 'num_awaiting_shipment',
            renderer: SkuUnitField,
          },
          { id: 'isActive', label: 'Is Active', ordering: 'is_active', renderer: BooleanField },
        ]}
      />
    </>
  )
}
